import { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Star, Quote, ChevronLeft, ChevronRight, Play, Building2 } from 'lucide-react';
import { useThemeStore } from '../../store/theme';
import PromoVideo from './PromoVideo';

const testimonials = [
  {
    id: 1,
    name: 'Sarah Chen',
    role: 'Marketing Director',
    company: 'TechFlow Inc',
    avatar: 'S',
    gradient: 'from-accent-purple to-accent-pink',
    rating: 5,
    text: 'SweetPilot transformed how we handle content creation. Aria generates weeks of social media content in minutes. Our engagement increased by 340% in just 3 months.',
    metric: '340%',
    metricLabel: 'Engagement Increase',
  },
  {
    id: 2,
    name: 'Michael Torres',
    role: 'Founder & CEO',
    company: 'ScaleUp Studio',
    avatar: 'M',
    gradient: 'from-accent-cyan to-accent-blue',
    rating: 5,
    text: "I replaced 3 freelancers with SweetPilot AI employees. The quality is better, it's faster, and I save over $4,000 per month. Best investment for my agency.",
    metric: '$4,000+',
    metricLabel: 'Monthly Savings',
  },
  {
    id: 3,
    name: 'Emma Thompson',
    role: 'Customer Success Lead',
    company: 'CloudServe',
    avatar: 'E',
    gradient: 'from-accent-green to-accent-cyan',
    rating: 5,
    text: 'Nova handles 80% of our support tickets automatically. Our response time dropped from 4 hours to 5 minutes. Customers are happier, and so is my team.',
    metric: '80%',
    metricLabel: 'Tickets Automated',
  },
  {
    id: 4,
    name: 'David Park',
    role: 'eCommerce Manager',
    company: 'StyleHouse',
    avatar: 'D',
    gradient: 'from-accent-orange to-accent-red',
    rating: 5,
    text: 'Flux manages our product listings across 5 platforms flawlessly. What used to take our team a full day now happens in under an hour.',
    metric: '95%',
    metricLabel: 'Time Saved',
  },
  {
    id: 5,
    name: 'Lisa Wang',
    role: 'HR Director',
    company: 'Innovate Labs',
    avatar: 'L',
    gradient: 'from-accent-pink to-accent-purple',
    rating: 5,
    text: 'Scout screens hundreds of resumes and finds the perfect candidates. We reduced our time-to-hire by 60% while improving candidate quality.',
    metric: '60%',
    metricLabel: 'Faster Hiring',
  },
  {
    id: 6,
    name: 'James Wilson',
    role: 'Sales VP',
    company: 'GrowthHQ',
    avatar: 'J',
    gradient: 'from-accent-blue to-accent-cyan',
    rating: 5,
    text: 'Spark writes personalized cold emails that actually get responses. Our reply rate jumped from 2% to 15%. The ROI is incredible.',
    metric: '15%',
    metricLabel: 'Reply Rate',
  },
];

const companyLogos = [
  'TechCorp', 'Innovate', 'CloudFirst', 'ScaleUp', 'GrowthIO', 'DataFlow',
  'FinanceHub', 'MediaPro', 'RetailMax', 'ServiceNow'
];

// Single testimonial card
const TestimonialCard = ({
  testimonial,
  isActive,
  isDark,
}: {
  testimonial: typeof testimonials[0];
  isActive: boolean;
  isDark: boolean;
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`
        relative rounded-3xl p-8 border transition-all duration-500
        ${isDark ? 'bg-dark-800' : 'bg-white shadow-lg'}
        ${isActive
          ? 'border-accent-purple/50 shadow-glow-sm'
          : isDark ? 'border-white/5 hover:border-white/20' : 'border-slate-200 hover:border-slate-300'
        }
      `}
    >
      {/* Quote icon */}
      <Quote className={`w-10 h-10 mb-4 ${isDark ? 'text-accent-purple/20' : 'text-accent-purple/30'}`} />

      {/* Rating */}
      <div className="flex gap-1 mb-4">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
        ))}
      </div>

      {/* Text */}
      <p className={`text-lg mb-6 leading-relaxed ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
        "{testimonial.text}"
      </p>

      {/* Metric */}
      <div className={`flex items-center gap-6 mb-6 p-4 rounded-xl ${isDark ? 'bg-dark-700/50' : 'bg-slate-50'}`}>
        <div>
          <div className="text-3xl font-bold text-accent-purple">{testimonial.metric}</div>
          <div className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{testimonial.metricLabel}</div>
        </div>
      </div>

      {/* Author */}
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center`}>
          <span className="text-lg font-bold text-white">{testimonial.avatar}</span>
        </div>
        <div>
          <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{testimonial.name}</div>
          <div className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            {testimonial.role} at {testimonial.company}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Video testimonial with modal - uses PromoVideo component
const VideoTestimonial = ({ isDark }: { isDark: boolean }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <>
      {/* Customer Stories Promo Video */}
      <PromoVideo
        isOpen={isPlaying}
        onClose={() => setIsPlaying(false)}
        type="stories"
      />

      {/* Video Thumbnail */}
      <div
        onClick={() => setIsPlaying(true)}
        className={`relative aspect-video rounded-3xl overflow-hidden border cursor-pointer group ${isDark ? 'bg-dark-800 border-white/5' : 'bg-white border-slate-200 shadow-lg'}`}
      >
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-dark-900/90 to-transparent' : 'bg-gradient-to-t from-slate-900/70 to-transparent'}`} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="w-20 h-20 rounded-full bg-accent-purple flex items-center justify-center shadow-glow-md mb-4 group-hover:shadow-glow-lg transition-all"
          >
            <Play className="w-8 h-8 text-white ml-1" fill="white" />
          </motion.div>
          <p className="text-white font-semibold">Watch Customer Stories</p>
          <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-300'}`}>25 sec</p>
        </div>
        {/* Placeholder thumbnail */}
        <div className="w-full h-full bg-gradient-to-br from-accent-purple/20 to-accent-cyan/20" />
      </div>
    </>
  );
};

export default function Testimonials() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  // Auto-rotate testimonials
  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoPlay]);

  const nextTestimonial = () => {
    setAutoPlay(false);
    setActiveIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setAutoPlay(false);
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section ref={ref} id="testimonials" className={`py-24 lg:py-32 relative overflow-hidden ${isDark ? 'bg-dark-800/30' : 'bg-slate-50'}`}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-accent-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-accent-cyan/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-green/10 border border-accent-green/20 rounded-full text-sm text-accent-green mb-6">
            <Star className="w-4 h-4 fill-accent-green" />
            Trusted by 50,000+ Users
          </div>
          <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Loved by Teams Worldwide
          </h2>
          <p className={`text-xl max-w-2xl mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
            See how businesses are transforming their workflows with AI employees
          </p>
        </motion.div>

        {/* Company logos */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.2 }}
          className="mb-16"
        >
          <p className={`text-center text-sm mb-6 uppercase tracking-wider ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            Trusted by innovative companies
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12 opacity-60">
            {companyLogos.map((company) => (
              <div
                key={company}
                className={`flex items-center gap-2 transition-colors ${isDark ? 'text-text-secondary hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
              >
                <Building2 className="w-5 h-5" />
                <span className="font-semibold">{company}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Main content */}
        <div className="grid lg:grid-cols-2 gap-8 items-center mb-12">
          {/* Featured testimonial */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            <AnimatePresence mode="wait">
              <TestimonialCard
                key={activeIndex}
                testimonial={testimonials[activeIndex]}
                isActive={true}
                isDark={isDark}
              />
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setActiveIndex(i);
                      setAutoPlay(false);
                    }}
                    className={`h-2 rounded-full transition-all ${
                      i === activeIndex
                        ? 'w-8 bg-accent-purple'
                        : isDark ? 'w-2 bg-dark-600 hover:bg-dark-500' : 'w-2 bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={prevTestimonial}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600' : 'bg-white hover:bg-slate-100 border border-slate-200'}`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextTestimonial}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600' : 'bg-white hover:bg-slate-100 border border-slate-200'}`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Video testimonial */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            <VideoTestimonial isDark={isDark} />
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {[
            { value: '50K+', label: 'Active Users', color: 'text-accent-purple' },
            { value: '11M+', label: 'Tasks Completed', color: 'text-accent-cyan' },
            { value: '4.9', label: 'Average Rating', color: 'text-yellow-400' },
            { value: '99.9%', label: 'Uptime', color: 'text-accent-green' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + i * 0.1 }}
              className={`text-center p-6 rounded-2xl border ${isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}
            >
              <div className={`text-4xl lg:text-5xl font-bold ${stat.color} mb-2`}>
                {stat.value}
              </div>
              <div className={isDark ? 'text-text-muted' : 'text-slate-500'}>{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
